import React, { useEffect, useMemo, useState } from "react";
import { API } from "../../config/api";
import "../Styles/Conceptimages.css";

export default function Conceptimages() {
  const [images, setImages] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("all");

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* =========================================================
     PAGINATION
  ========================================================= */

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  /* =========================================================
     IMAGE POPUP
  ========================================================= */

  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  /* =========================================================
     FETCH ALL IMAGES
  ========================================================= */

  const fetchImages = async () => {
    try {
      setLoading(true);
      setError("");

      const url = `${API.BASE_URL}/DeleteConcepts.php?action=all_images`;

      console.log("Concept Images API:", url);

      const response = await fetch(url);
      const text = await response.text();

      console.log("Concept Images Response:", text);

      let result;

      try {
        result = JSON.parse(text);
      } catch (jsonError) {
        throw new Error(`Invalid JSON response: ${text.substring(0, 300)}`);
      }

      if (!response.ok) {
        throw new Error(result.message || `HTTP Error ${response.status}`);
      }

      if (!result.success) {
        throw new Error(result.message || "Unable to load concept images.");
      }

      const imageList = result.data?.images || [];

      setImages(imageList);

      /* GET UNIQUE FOLDERS */

      const uniqueFolders = [
        ...new Set(imageList.map((image) => image.folder).filter(Boolean)),
      ].sort();

      setFolders(uniqueFolders);
    } catch (error) {
      console.error("Conceptimages fetch error:", error);

      setError(error.message || "Unable to load concept images.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    fetchImages();
  }, []);

  /* =========================================================
     FILTER IMAGES
  ========================================================= */

  const filteredImages = useMemo(() => {
    if (selectedFolder === "all") {
      return images;
    }

    return images.filter((image) => image.folder === selectedFolder);
  }, [images, selectedFolder]);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const totalPages = Math.ceil(filteredImages.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;

  const endIndex = startIndex + itemsPerPage;

  const paginatedImages = filteredImages.slice(startIndex, endIndex);

  /* =========================================================
     PAGE NUMBERS
  ========================================================= */

  const pageNumbers = [];

  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  /* =========================================================
     RESET PAGE WHEN FILTER CHANGES
  ========================================================= */

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFolder, itemsPerPage]);

  /* =========================================================
     KEEP PAGE VALID AFTER DELETE
  ========================================================= */

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  /* =========================================================
     DELETE IMAGE
  ========================================================= */

  const deleteImage = async (image) => {
    try {
      setDeleting(`${image.folder}-${image.name}`);

      setError("");
      setSuccess("");

      const response = await fetch(`${API.BASE_URL}/DeleteConcepts.php`, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },

        body: JSON.stringify({
          action: "delete_image",
          folder: image.folder,
          file: image.name,
        }),
      });

      const text = await response.text();

      console.log("Delete Concept Response:", text);

      let result;

      try {
        result = JSON.parse(text);
      } catch (jsonError) {
        throw new Error(`Invalid JSON response: ${text.substring(0, 300)}`);
      }

      if (!response.ok) {
        throw new Error(result.message || `HTTP Error ${response.status}`);
      }

      if (!result.success) {
        throw new Error(result.message || "Unable to delete image.");
      }

      /* CLOSE POPUP IF THIS IMAGE IS OPEN */

      setSelectedImageIndex(null);

      /* REMOVE IMAGE */

      setImages((previous) =>
        previous.filter(
          (item) => !(item.folder === image.folder && item.name === image.name),
        ),
      );

      setSuccess(`"${image.name}" deleted successfully.`);
    } catch (error) {
      console.error("Delete image error:", error);

      setError(error.message || "Unable to delete image.");
    } finally {
      setDeleting(null);
    }
  };

  /* =========================================================
     OPEN IMAGE POPUP
  ========================================================= */

  const openImage = (index) => {
    setSelectedImageIndex(index);

    document.body.classList.add("concept-lightbox-open");
  };

  /* =========================================================
     CLOSE IMAGE POPUP
  ========================================================= */

  const closeImage = () => {
    setSelectedImageIndex(null);

    document.body.classList.remove("concept-lightbox-open");
  };

  /* =========================================================
     NEXT IMAGE
  ========================================================= */

  const nextImage = () => {
    if (selectedImageIndex === null || paginatedImages.length === 0) {
      return;
    }

    setSelectedImageIndex((previousIndex) =>
      previousIndex === paginatedImages.length - 1 ? 0 : previousIndex + 1,
    );
  };

  /* =========================================================
     PREVIOUS IMAGE
  ========================================================= */

  const previousImage = () => {
    if (selectedImageIndex === null || paginatedImages.length === 0) {
      return;
    }

    setSelectedImageIndex((previousIndex) =>
      previousIndex === 0 ? paginatedImages.length - 1 : previousIndex - 1,
    );
  };

  /* =========================================================
     KEYBOARD CONTROLS
  ========================================================= */

  useEffect(() => {
    const handleKeyDown = (event) => {
      /* Popup is not open */

      if (selectedImageIndex === null) {
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        nextImage();
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        previousImage();
      }

      if (event.key === "Escape") {
        event.preventDefault();
        closeImage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImageIndex, paginatedImages.length]);

  /* =========================================================
     CLEAN BODY CLASS
  ========================================================= */

  useEffect(() => {
    return () => {
      document.body.classList.remove("concept-lightbox-open");
    };
  }, []);

  /* =========================================================
     CHANGE PAGE
  ========================================================= */

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) {
      return;
    }

    closeImage();

    setCurrentPage(page);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div className="concept-images-page">
        <div className="concept-images-loading">
          <span className="spinner-border" role="status" />

          <span>Loading concept images...</span>
        </div>
      </div>
    );
  }

  /* =========================================================
     CURRENT POPUP IMAGE
  ========================================================= */

  const activeImage =
    selectedImageIndex !== null ? paginatedImages[selectedImageIndex] : null;

  /* =========================================================
     PAGE
  ========================================================= */

  return (
    <div className="concept-images-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="concept-images-header">
        <div>
          <h4 className="concept-images-title">Concept Images</h4>

          <div className="concept-images-subtitle">
            Manage and delete uploaded concept images
          </div>
        </div>

        {/* ===================================================
            FOLDER FILTER
        =================================================== */}

        <div className="concept-images-filter">
          <label>Filter by Folder</label>

          <select
            className="form-select"
            value={selectedFolder}
            onChange={(event) => setSelectedFolder(event.target.value)}
          >
            <option value="all">All Folders</option>

            {folders.map((folder) => (
              <option key={folder} value={folder}>
                {folder}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* =====================================================
          SUCCESS
      ===================================================== */}

      {success && (
        <div className="concept-images-alert concept-images-alert-success">
          <i className="bi bi-check-circle-fill"></i>

          <span>{success}</span>
        </div>
      )}

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <div className="concept-images-alert concept-images-alert-danger">
          <i className="bi bi-exclamation-circle-fill"></i>

          <span>{error}</span>
        </div>
      )}

      {/* =====================================================
          IMAGE COUNT
      ===================================================== */}

      {filteredImages.length > 0 && (
        <div className="concept-images-toolbar">
          <div className="concept-images-count">
            Showing <strong>{startIndex + 1}</strong>
            {" - "}
            <strong>{Math.min(endIndex, filteredImages.length)}</strong>
            {" of "}
            <strong>{filteredImages.length}</strong>
            {" images"}
          </div>

          <div className="concept-items-control">
            <span>Images per page</span>

            <select
              className="form-select"
              value={itemsPerPage}
              onChange={(event) => setItemsPerPage(Number(event.target.value))}
            >
              <option value={25}>25</option>

              <option value={50}>50</option>

              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}

      {/* =====================================================
          IMAGE GRID
      ===================================================== */}

      {filteredImages.length === 0 ? (
        <div className="concept-images-empty">
          <div className="concept-empty-icon">
            <i className="bi bi-images"></i>
          </div>

          <div className="concept-empty-title">No images found</div>

          <p className="concept-empty-text">
            There are no concept images in this folder.
          </p>
        </div>
      ) : (
        <>
          <div className="concept-images-grid">
            {paginatedImages.map((image, index) => {
              const deleteKey = `${image.folder}-${image.name}`;

              const isDeleting = deleting === deleteKey;

              return (
                <div className="concept-image-card" key={deleteKey}>
                  {/* IMAGE */}

                  <div
                    className="concept-grid-image"
                    onClick={() => openImage(index)}
                  >
                    <img src={image.url} alt="Concept" loading="lazy" />

                    {/* DELETE ICON */}

                    <button
                      type="button"
                      className="concept-image-delete-icon"
                      title="Delete image"
                      aria-label="Delete image"
                      disabled={isDeleting}
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteImage(image);
                      }}
                    >
                      {isDeleting ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                      ) : (
                        <i className="bi bi-trash3-fill"></i>
                      )}
                    </button>

                    {/* CLICK TO VIEW INDICATOR */}

                    <div className="concept-image-view-icon">
                      <i className="bi bi-arrows-fullscreen"></i>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* =================================================
              PAGINATION
          ================================================= */}

          {totalPages > 1 && (
            <div className="concept-pagination">
              {/* PREVIOUS */}

              <button
                type="button"
                className="concept-page-button concept-page-arrow"
                disabled={currentPage === 1}
                onClick={() => goToPage(currentPage - 1)}
              >
                <i className="bi bi-chevron-left"></i>
              </button>

              {/* PAGE NUMBERS */}

              {pageNumbers.map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`concept-page-button ${
                    currentPage === page ? "active" : ""
                  }`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}

              {/* NEXT */}

              <button
                type="button"
                className="concept-page-button concept-page-arrow"
                disabled={currentPage === totalPages}
                onClick={() => goToPage(currentPage + 1)}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* =====================================================
          IMAGE LIGHTBOX / POPUP
      ===================================================== */}

      {activeImage && (
        <div className="concept-image-lightbox" onClick={closeImage}>
          {/* CLOSE */}

          <button
            type="button"
            className="concept-lightbox-close"
            onClick={closeImage}
            aria-label="Close image"
            title="Close"
          >
            <i className="bi bi-x-lg"></i>
          </button>

          {/* PREVIOUS */}

          {paginatedImages.length > 1 && (
            <button
              type="button"
              className="concept-lightbox-nav concept-lightbox-prev"
              onClick={(event) => {
                event.stopPropagation();
                previousImage();
              }}
              aria-label="Previous image"
              title="Previous image"
            >
              <i className="bi bi-chevron-left"></i>
            </button>
          )}

          {/* IMAGE AREA */}

          <div
            className="concept-lightbox-content"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={activeImage.url}
              alt="Concept Preview"
              className="concept-lightbox-image"
            />

            {/* IMAGE POSITION */}

            <div className="concept-lightbox-counter">
              {selectedImageIndex + 1}
              {" / "}
              {paginatedImages.length}
            </div>
          </div>

          {/* NEXT */}

          {paginatedImages.length > 1 && (
            <button
              type="button"
              className="concept-lightbox-nav concept-lightbox-next"
              onClick={(event) => {
                event.stopPropagation();
                nextImage();
              }}
              aria-label="Next image"
              title="Next image"
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
